import React from 'react';
import type { TFunction } from 'i18next';
import { type Appointment, type CalendarEvent, type Doctor, type Patient } from './types';
import { getReservationFeeState } from './reservationFee';
import { formatTime } from './utils';
import { useTheme } from '../../theme/ThemeContext';

interface AppointmentHoverDetailsProps {
  appointment: Appointment;
  patient?: Patient;
  prospectEvent?: CalendarEvent;
  anchorRect: DOMRect;
  t: TFunction;
  patientDisplayName: string;
  effectiveHc?: string;
  doctor?: Doctor;
  secondDoctor?: Doctor;
  reasonLabel: string;
  hasCriticalAllergy: boolean;
  needsHcWarning: boolean;
  showReservationFeeNotPaid: boolean;
  hasPendingPayment: boolean;
  pendingPaymentTitle: string;
  isNoShow: boolean;
  isCanceled: boolean;
  noShowCount: number;
}

const money = (value: number | null | undefined) => `Bs ${Number(value || 0).toFixed(2)}`;

const doctorName = (doctor?: Doctor) => {
  if (!doctor) return '';
  return `${doctor.firstName || ''} ${doctor.lastName || ''}`.trim();
};

const isPresent = (value: unknown): value is string | number => {
  if (typeof value === 'number') return true;
  return typeof value === 'string' && value.trim().length > 0;
};

export const AppointmentHoverDetails: React.FC<AppointmentHoverDetailsProps> = ({
  appointment,
  patient,
  prospectEvent,
  anchorRect,
  t,
  patientDisplayName,
  effectiveHc,
  doctor,
  secondDoctor,
  reasonLabel,
  hasCriticalAllergy,
  needsHcWarning,
  showReservationFeeNotPaid,
  hasPendingPayment,
  pendingPaymentTitle,
  isNoShow,
  isCanceled,
  noShowCount
}) => {
  const { tokens } = useTheme();
  const width = 360;
  const margin = 12;
  const viewportWidth = typeof window === 'undefined' ? 1024 : window.innerWidth;
  const viewportHeight = typeof window === 'undefined' ? 768 : window.innerHeight;
  const maxHeight = Math.max(220, Math.min(480, viewportHeight - margin * 2));
  const left = Math.max(margin, Math.min(anchorRect.left, viewportWidth - width - margin));
  const belowTop = anchorRect.bottom + 8;
  const aboveTop = anchorRect.top - maxHeight - 8;
  const top = belowTop + maxHeight <= viewportHeight - margin
    ? belowTop
    : Math.max(margin, aboveTop);

  const isProspect = !!appointment.isProspect;
  const reservationFeeState = getReservationFeeState(appointment);
  const reservationFeePaid = appointment.reservationFeePaidAmount ?? (reservationFeeState.configured ? reservationFeeState.paid : null);
  const reservationFeeBalance = appointment.reservationFeeBalance ?? (reservationFeeState.configured ? reservationFeeState.balance : null);
  const reservationFeeCredit = appointment.reservationFee?.availableCredit ?? 0;
  const start = appointment.startTime ? new Date(appointment.startTime) : null;
  const end = appointment.endTime ? new Date(appointment.endTime) : null;
  const timeLabel = start && end ? `${formatTime(start)} - ${formatTime(end)}` : '';
  const tags = [
    appointment.status === 'confirmed' && !isNoShow && !isCanceled
      ? t('calendar.lifecycle.status.confirmed', 'Confirmed')
      : '',
    needsHcWarning ? t('calendar.hover.tagMissingHc', 'Missing HC') : '',
    hasCriticalAllergy ? t('calendar.hover.tagAllergy', 'Critical allergy') : '',
    showReservationFeeNotPaid
      ? 'R! - ' + t('calendar.lifecycle.reservationPendingAmount', 'Reservation pending: Bs {{amount}}').replace('{{amount}}', reservationFeeState.balance.toFixed(2))
      : '',
    hasPendingPayment ? '$ - ' + t('calendar.hasPendingBalance', 'Has pending balance') : '',
    appointment.notes ? t('calendar.hover.tagNotes', 'Has notes') : '',
    appointment.status === 'stand-by' ? t('calendar.standBy', 'Stand-by') : '',
    isNoShow ? t('calendar.markedAsNoShow', 'Marked as no-show') : '',
    isCanceled ? t('calendar.hover.tagCanceled', 'Canceled') : ''
  ].filter(Boolean);

  const rows: Array<[string, string | number]> = isProspect
    ? [
        [t('calendar.hover.prospect', 'Prospect'), appointment.prospectName || t('calendar.newPatientIntake', 'New Patient Intake')],
        [t('calendar.contact', 'Contact'), appointment.prospectContact || ''],
        [t('calendar.hover.time', 'Time'), timeLabel],
        [t('calendar.hover.doctor', 'Doctor'), doctorName(doctor) || t('calendar.unassignedDoctor', 'No doctor assigned')],
        [t('calendar.room', 'Room'), appointment.room ? String(appointment.room) : ''],
        [t('calendar.notes', 'Notes'), appointment.prospectNotes || appointment.notes || prospectEvent?.notes || '']
      ].filter(([, value]) => isPresent(value)) as Array<[string, string | number]>
    : [
        [t('calendar.hover.patient', 'Patient'), patientDisplayName],
        [t('calendar.hover.hc', 'HC'), effectiveHc || ''],
        [t('calendar.contact', 'Contact'), appointment.patient?.email || patient?.email || ''],
        [t('calendar.hover.patientStatus', 'Patient status'), String(appointment.patient?.status || patient?.status || '')],
        [t('calendar.hover.time', 'Time'), timeLabel],
        [t('calendar.room', 'Room'), appointment.room ? String(appointment.room) : ''],
        [t('calendar.hover.doctor', 'Doctor'), doctorName(doctor) || t('calendar.unassignedDoctor', 'No doctor assigned')],
        [t('calendar.hover.secondDoctor', 'Second doctor'), doctorName(secondDoctor)],
        [t('calendar.hover.reason', 'Reason'), reasonLabel],
        [t('calendar.notes', 'Notes'), appointment.notes || ''],
        [t('calendar.hover.cancellationNote', 'Cancellation note'), appointment.cancellationNote || ''],
        [t('calendar.hover.noShows', 'No-shows'), noShowCount > 0 ? noShowCount : '']
      ].filter(([, value]) => isPresent(value)) as Array<[string, string | number]>;

  const treatmentNames = [
    ...(appointment.treatments || []).map(treatment => treatment?.name).filter(Boolean),
    appointment.treatment?.name,
    appointment.treatmentTemplate?.name
  ].filter((name, index, list): name is string => !!name && list.indexOf(name) === index);

  const financeRows: Array<[string, string | number]> = [
    [t('calendar.hover.charge', 'Charge'), appointment.chargeAmount == null ? '' : money(appointment.chargeAmount)],
    [t('calendar.hover.amountPaid', 'Amount paid'), appointment.amountPaid == null ? '' : money(appointment.amountPaid)],
    [t('calendar.hover.appointmentBalanceStatus', 'Appointment balance status'), appointment.paymentStatus ? t(`calendar.finance.statuses.${appointment.paymentStatus}`, appointment.paymentStatus) : ''],
    [t('calendar.lifecycle.reservationFee', 'Reservation Fee'), reservationFeeState.configured ? money(reservationFeeState.amount) : ''],
    [t('calendar.hover.reservationFeeStatus', 'Reservation fee status'), reservationFeeState.configured ? t(`calendar.hover.reservationStatuses.${reservationFeeState.status}`, reservationFeeState.status) : ''],
    [t('calendar.hover.reservationPaid', 'Reservation paid'), reservationFeePaid == null ? '' : money(reservationFeePaid)],
    [t('calendar.hover.reservationBalance', 'Reservation balance'), reservationFeeBalance == null ? '' : money(reservationFeeBalance)],
    [t('calendar.hover.reservationCredit', 'Reservation credit'), reservationFeeState.configured ? money(reservationFeeCredit) : ''],
    [t('calendar.hover.debtSummary', 'Debt summary'), hasPendingPayment ? pendingPaymentTitle : '']
  ].filter(([, value]) => isPresent(value)) as Array<[string, string | number]>;

  return (
    <div
      role="tooltip"
      data-testid="appointment-hover-details"
      style={{
        position: 'fixed',
        top,
        left,
        width,
        maxHeight,
        overflowY: 'auto',
        pointerEvents: 'none',
        zIndex: 5000,
        background: tokens.app.surface,
        color: tokens.app.text,
        border: `1px solid ${tokens.app.border}`,
        boxShadow: tokens.app.shadow,
        borderRadius: 6,
        padding: 12,
        fontSize: 12,
        lineHeight: 1.35
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 8 }}>
        {isProspect ? (appointment.prospectName || t('calendar.newPatientIntake', 'New Patient Intake')) : patientDisplayName}
      </div>

      {tags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {tags.map(tag => (
            <span key={tag} style={{ background: tokens.app.subtle, color: tokens.app.text, border: `1px solid ${tokens.app.border}`, borderRadius: 999, padding: '2px 7px', fontSize: 11, fontWeight: 700 }}>
              {tag}
            </span>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '104px 1fr', gap: '5px 8px' }}>
        {rows.map(([label, value]) => (
          <React.Fragment key={label}>
            <div style={{ color: tokens.app.muted, fontWeight: 700 }}>{label}</div>
            <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', color: hasPendingPayment && (label === t('calendar.hover.debtSummary', 'Debt summary') || label === t('calendar.hover.reservationBalance', 'Reservation balance')) ? '#ef4444' : undefined }}>{value}</div>
          </React.Fragment>
        ))}
      </div>

      {treatmentNames.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ color: tokens.app.muted, fontWeight: 700, marginBottom: 4 }}>{t('calendar.hover.treatments', 'Treatments')}</div>
          <div>{treatmentNames.join(', ')}</div>
        </div>
      )}

      {financeRows.length > 0 && (
        <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '104px 1fr', gap: '5px 8px' }}>
          {financeRows.map(([label, value]) => (
            <React.Fragment key={label}>
              <div style={{ color: tokens.app.muted, fontWeight: 700 }}>{label}</div>
              <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{value}</div>
            </React.Fragment>
          ))}
        </div>
      )}

      {appointment.financialSummary?.treatments?.length ? (
        <div style={{ marginTop: 10 }}>
          <div style={{ color: tokens.app.muted, fontWeight: 700, marginBottom: 4 }}>{t('calendar.hover.balances', 'Treatment balances')}</div>
          {appointment.financialSummary.treatments.map(treatment => (
            <div key={treatment.treatmentId} style={{ marginTop: 3, color: treatment.totalDue > 0.005 ? '#ef4444' : undefined }}>
              {treatment.treatmentName}: {money(treatment.totalDue)}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
